import React, { useEffect, useState } from 'react';
import { Colors } from 'library';
import { Checkbox, Divider, PillButton, Text, Title, Stack, Card, Row } from 'library/ui';
import { client } from 'shared';
import moment from 'moment';
import { add } from 'date-fns';

import FormField from 'parent-portal/forms/components/FormField';
import BusinessDateAndTimes from 'parent-portal/forms/components/pages/DateAndTimePage/BusinessDatesAndTimes';
import RequestedWorkersOnJob from 'parent-portal/forms/components/pages/ProviderPreferencesPage/RequestedWorkersOnJob';
import { useUserContext } from 'UserContext';
import { useJobFormContext } from 'parent-portal/forms/JobFormContext';
import { tandemRecommendedDescription } from 'parent-portal/forms/components/pages/HeadlineDescriptionPage/JobDescriptionInput';
import { coerceHours } from 'parent-portal/forms/JobRequestSubmission';
import { timeWithDate } from 'parent-portal/forms/time-operations';
import { convertPreferredSitterObject } from 'parent-portal/forms/components/pages/ProviderPreferencesPage/preferredWorkersApi';
import { Warning } from '@mui/icons-material';
import { IJobPaymentError } from 'models';
import JobPaymentErrorDisplay from 'parent-portal/forms/components/JobPaymentErrorDisplay';

export function AutoHireWorkerInnerDialog({
    setOpen,
    addressId,
    defaultDay,
}: {
    setOpen: (open: boolean) => void;
    addressId?: number;
    defaultDay?: Date;
}) {
    const [valid, setValid] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | undefined>();
    const [paymentError, setPaymentError] = useState<IJobPaymentError>();
    const [locationName, setLocationName] = useState<string | undefined>();
    const { form } = useJobFormContext();
    const { user } = useUserContext();
    const [confirmSelected, setConfirmSelected] = useState(false);
    const [workerName, setWorkerName] = useState<string | undefined>();
    const [isVpkLocation, setIsVpkLocation] = useState(false);
    const [isVpkClassroom, setIsVpkClassroom] = useState(false);

    useEffect(() => {
        const worker = form.fieldStates.preferredWorkers.value[0] ?? form.fieldStates.pastWorkers.value[0];
        setWorkerName(worker?.name);
    }, [form.fieldStates.preferredWorkers.value, form.fieldStates.pastWorkers.value]);

    useEffect(() => {
        form.fieldStates.businessJobType.setValue('SUBSTITUTE');
        form.fieldStates.businessJobType.setValid(true);
        form.fieldStates.lastHireOffsetMinutes.setValid(true);
        form.fieldStates.headline.setValue('Teacher');
        form.fieldStates.comments.setValue(tandemRecommendedDescription);
        form.fieldStates.startDate.setValue(defaultDay ? moment(defaultDay) : moment(add(new Date(), { days: 1 })));
        form.fieldStates.endDate.setValue(defaultDay ? moment(defaultDay) : moment(add(new Date(), { days: 1 })));
    }, []);

    useEffect(() => {
        const addresses = user?.address;
        if (addresses?.length) {
            const selectedAddress = addresses.find((x) => x.id === addressId);

            const mailingAddress = selectedAddress ?? addresses.find((x) => x.address_type === 'PRIMARY');
            const foundAddressId = mailingAddress ? mailingAddress.id : addresses[0].id;
            const busLocationId = mailingAddress?.business_location
                ? mailingAddress.business_location.id
                : addresses[0]?.business_location?.id;

            setLocationName(
                mailingAddress?.business_location?.name ?? addresses[0]?.business_location?.name ?? undefined,
            );
            setIsVpkLocation(
                mailingAddress?.business_location?.is_vpk_location ??
                    addresses[0]?.business_location?.is_vpk_location ??
                    false,
            );

            form.fieldStates.address.setValue([{ id: foundAddressId, businessLocationId: busLocationId }]);
        }
    }, [user]);

    function onClick() {
        setError(undefined);
        setPaymentError(undefined);
        setLoading(true);

        const {
            daysOfTheWeek,
            sameTimes,
            startDate,
            endDate,
            jobTimes,
            address,
            headline,
            comments,
            preferredWorkers,
            pastWorkers,
        } = form.toValues();

        const { selectedWeekDays, ongoingTimesOfDay } = coerceHours({
            daysOfTheWeek,
            sameTimes: sameTimes,
            start: sameTimes ? jobTimes.start && timeWithDate(jobTimes.start!, startDate!) : startDate,
            end: endDate,
            end_time: jobTimes.end,
            selectedPastWorkers: [],
            selectedPreferredWorkers: [],
            paymentSchedule: undefined,
            faqs: [],
        });

        const chosenSitters = convertPreferredSitterObject(preferredWorkers, pastWorkers);

        client('api/ongoing/create-and-hire/', {
            method: 'POST',
            body: {
                start_date: startDate?.format('YYYY-MM-DD'),
                end_date: endDate?.format('YYYY-MM-DD'),
                days_of_week: selectedWeekDays,
                times_of_day: ongoingTimesOfDay,
                headline,
                family_comments: comments,
                preferred_sitters: chosenSitters,
                address_id: address?.[0]?.id,
                business_location_id: address?.[0]?.businessLocationId,
                family_id: user?.family?.[0]?.id,
                transportation: false,
                is_vpk_classroom: isVpkClassroom,
            },
        })
            .then(() => {
                setLoading(false);
                setOpen(false);
                window.location.reload();
            })
            .catch((error) => {
                setLoading(false);
                const errorText = error?.errors?.length ? error.errors.map((x: string) => x).join(' ') : '';
                if (errorText) {
                    setError(
                        `${errorText ? `The following went wrong creating the request: ${errorText} ` : ''}Please try again or contact support.`,
                    );
                } else if (error?.payment) {
                    setPaymentError(error?.payment);
                } else {
                    setError('There was an error creating the request. Please try again or contact support.');
                }
            });
    }

    return (
        <Stack gap="none">
            <Title size="h2">Automatically schedule worker</Title>
            {locationName && <Text weight="bold">{locationName}</Text>}

            <Card tone="warning">
                <Text weight="bold" tone="warning">
                    <Warning htmlColor={Colors.warning} />{' '}
                    <u>The person you select must have already agreed to this shift.</u> You must have communicated
                    already with them in person or via another communication medium. If you would like to request a
                    worker instead, please use the &#34;Post Shift&#34; button.
                </Text>
            </Card>
            <BusinessDateAndTimes setIsValid={setValid} essentialOnly />
            <Divider />
            <FormField title={'4. Schedule worker'} style={{ marginTop: 30 }}>
                <RequestedWorkersOnJob multiple={false} />
            </FormField>
            {isVpkLocation && (
                <FormField style={{ marginBottom: 10 }}>
                    <Checkbox
                        label="Will the worker be in a VPK classroom?"
                        checked={isVpkClassroom}
                        onChange={setIsVpkClassroom}
                    />
                </FormField>
            )}
            {!!error && (
                <Text tone="error" size="sm">
                    {error}
                </Text>
            )}
            <Checkbox
                label={`I have already communicated with ${workerName ?? 'the worker'} and they have agreed to work the entered times.`}
                checked={confirmSelected}
                onChange={setConfirmSelected}
            />
            {paymentError ? <JobPaymentErrorDisplay paymentError={paymentError} /> : null}
            <Row justify="end">
                <PillButton
                    disabled={
                        loading ||
                        !valid ||
                        !form.fieldStates.pastWorkers.isValid ||
                        !form.fieldStates.preferredWorkers.isValid ||
                        !confirmSelected
                    }
                    onClick={onClick}
                >
                    Submit
                </PillButton>
            </Row>
        </Stack>
    );
}
